import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { toast } from 'sonner';
import {
  Loader2, Sparkles, ChevronDown, ChevronUp, Save, Trash2,
  FileText, CheckCircle2, XCircle, RefreshCw, ImageIcon,
  Search, Lock, X, Plus, Send, Edit3, List, ArrowLeft, Filter, Upload, HelpCircle
} from 'lucide-react';
import EmptyState from '../components/EmptyState';
import { getScoreColor } from '../utils/agentColors';
import ConfigDialog from '../components/ConfigDialog';
import { ProcessBar, GenerationSteps } from '../components/ProcessBar';

const LENGTHS = [
  { value: 'short', label: 'Breve (150 parole)' },
  { value: 'medium', label: 'Medio (300 parole)' },
  { value: 'long', label: 'Lungo (500+ parole)' },
];
const LANGUAGES = [
  { value: 'it', label: 'Italiano' },
  { value: 'en', label: 'English' },
  { value: 'de', label: 'Deutsch' },
  { value: 'fr', label: 'Francais' },
];

const MEMORY_FLAGS = [
  { key: 'prevRubrica', label: 'Post precedenti stessa rubrica', desc: 'Ultimi 3-5 per coerenza', defaultOn: true },
  { key: 'rejected', label: 'Post scartati/rifiutati', desc: 'Per non ripetere errori', defaultOn: true },
  { key: 'modified', label: 'Post modificati dall\'utente', desc: 'Impara il tuo stile', defaultOn: true },
  { key: 'editorial', label: 'Piano editoriale corrente', desc: 'Evita sovrapposizioni tema', defaultOn: false },
  { key: 'events', label: 'Eventi/corsi in arrivo', desc: 'Riferimenti coerenti', defaultOn: false },
  { key: 'performance', label: 'Performance precedenti', desc: 'Privilegia ciò che funziona', defaultOn: false },
];

/* ─── Tag Input reusable ─── */
function TagInput({ tags, onChange, placeholder, suggestions, pillColor }) {
  const [input, setInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const filtered = suggestions?.filter(s =>
    s.toLowerCase().includes(input.toLowerCase()) && !tags.includes(s)
  ) || [];

  const addTag = (tag) => {
    if (tag && !tags.includes(tag)) onChange([...tags, tag]);
    setInput('');
    setShowSuggestions(false);
  };
  const removeTag = (tag) => onChange(tags.filter(t => t !== tag));

  return (
    <div className="relative">
      <div className="flex flex-wrap gap-1 p-1.5 rounded-md border border-input bg-background min-h-[36px] items-center">
        {tags.map(t => (
          <span key={t} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium text-white"
            style={{ backgroundColor: pillColor || '#64748B' }}>
            {t}
            <button onClick={() => removeTag(t)} className="hover:opacity-70"><X className="w-3 h-3" /></button>
          </span>
        ))}
        <input className="flex-1 min-w-[80px] bg-transparent text-xs outline-none placeholder:text-muted-foreground px-1"
          value={input} onChange={e => { setInput(e.target.value); setShowSuggestions(true); }}
          onKeyDown={e => { if (e.key === 'Enter' && input.trim()) { e.preventDefault(); addTag(input.trim()); } }}
          placeholder={tags.length === 0 ? placeholder : ''} />
      </div>
      {showSuggestions && filtered.length > 0 && (
        <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border rounded-md shadow-lg max-h-32 overflow-y-auto">
          {filtered.slice(0, 5).map(s => (
            <button key={s} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50" onClick={() => addTag(s)}>{s}</button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Contact Autocomplete ─── */
function ContactAutocomplete({ tags, onChange }) {
  const [input, setInput] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  const search = (q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim()) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get(`/crm/contacts?search=${encodeURIComponent(q)}&limit=5`);
        setResults(res.data?.contacts || res.data || []);
      } catch { setResults([]); }
      setSearching(false);
    }, 300);
  };

  const addPerson = (c) => {
    const name = c.full_name || c.name || c.email;
    if (name && !tags.includes(name)) onChange([...tags, name]);
    setInput('');
    setResults([]);
  };
  const removePerson = (name) => onChange(tags.filter(t => t !== name));

  return (
    <div className="relative">
      <div className="flex flex-wrap gap-1 p-1.5 rounded-md border border-input bg-background min-h-[36px] items-center">
        {tags.map(t => (
          <span key={t} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-blue-100 text-blue-800">
            {t}
            <button onClick={() => removePerson(t)} className="hover:opacity-70"><X className="w-3 h-3" /></button>
          </span>
        ))}
        <input className="flex-1 min-w-[80px] bg-transparent text-xs outline-none placeholder:text-muted-foreground px-1"
          value={input} onChange={e => { setInput(e.target.value); search(e.target.value); }}
          onKeyDown={e => { if (e.key === 'Enter' && input.trim()) { e.preventDefault(); addPerson({ name: input.trim() }); } }}
          placeholder={tags.length === 0 ? 'Cerca contatti...' : ''} />
      </div>
      {(results.length > 0 || searching) && (
        <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border rounded-md shadow-lg max-h-40 overflow-y-auto">
          {searching && <div className="px-3 py-2 text-xs text-muted-foreground">Cercando...</div>}
          {results.map((c, i) => (
            <button key={c.id || i} className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center justify-between"
              onClick={() => addPerson(c)}>
              <span className="font-medium">{c.full_name || c.name}</span>
              {c.company && <span className="text-muted-foreground">{c.company}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Section wrapper with completion ─── */
function FormSection({ title, defaultOpen = false, filled, total, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button className="w-full flex items-center justify-between py-2.5 px-4 text-[12px] font-semibold uppercase tracking-wider hover:bg-gray-50 transition-colors rounded-t-lg"
          style={{ color: 'var(--color-primary)' }} data-testid={`section-${title.toLowerCase().replace(/\s/g,'-')}`}>
          <span>{title}</span>
          <div className="flex items-center gap-2">
            {!open && filled !== undefined && (
              <span className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                style={{
                  background: filled === total ? '#E6F3DE' : 'var(--color-background-secondary, #F3F4F6)',
                  color: filled === total ? '#3B6D11' : 'var(--color-muted)',
                }}>
                {filled}/{total}
              </span>
            )}
            {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-4 pb-4 space-y-2.5">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */
export default function ContentStudioPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);

  // ─── View mode: 'create' or 'list' ───
  const [viewMode, setViewMode] = useState('create');
  const [contentList, setContentList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listFilter, setListFilter] = useState('all');

  // ─── Config data ───
  const [objectives, setObjectives] = useState([]);
  const [targets, setTargets] = useState([]);
  const [structures, setStructures] = useState([]);
  const [hooks, setHooks] = useState([]);
  const [ctas, setCtas] = useState([]);
  const [rubriche, setRubriche] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [events, setEvents] = useState([]);
  const [products, setProducts] = useState([]);
  const [brandContext, setBrandContext] = useState({});
  const [sequences, setSequences] = useState([]);
  const [channels, setChannels] = useState([]);

  // ─── UI state ───
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [draftId, setDraftId] = useState(null);
  const [savedDraft, setSavedDraft] = useState(null);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [contentType, setContentType] = useState('post');
  const [activeSequence, setActiveSequence] = useState(null);
  const [lockedFields, setLockedFields] = useState({});
  const [scoutOpen, setScoutOpen] = useState(false);
  const [scoutReports, setScoutReports] = useState([]);
  const [expandedReport, setExpandedReport] = useState(null);
  const [imageSheetOpen, setImageSheetOpen] = useState(false);
  const [imageAssets, setImageAssets] = useState([]);
  const [imageSearch, setImageSearch] = useState('');
  const [imageFolder, setImageFolder] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenNote, setRegenNote] = useState('');
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [scoreExpanded, setScoreExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [configDialogType, setConfigDialogType] = useState(null);
  const [genStep, setGenStep] = useState(0);
  const [dismissedSuggestion, setDismissedSuggestion] = useState(false);
  const autoSaveTimer = useRef(null);

  // ─── Form state ───
  const [form, setForm] = useState({
    topic: '', objective_id: '', target_ids: [], rubrica_id: '',
    structure_id: '', hook_type_id: '', cta_type_id: '',
    length: 'medium', language: 'it', emoji_level: 1,
    channels: [], linked_event_id: '', linked_product_id: '',
    inspiration_source: '', cite_sources: [], tagged_people: [],
    hashtags: [], tone_override: '', positioning_override: '',
    keywords_add: [], keywords_avoid_add: [],
    memory_flags: { prevRubrica: true, rejected: true, modified: true, editorial: false, events: false, performance: false },
    scheduled_date: '', scheduled_time: '',
    sequence_id: '', sequence_number: 1, free_sequence: false, free_sequence_count: 1,
  });

  const updateForm = (field, value) => {
    if (lockedFields[field]) return;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // ─── 12 parallel API calls ───
  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const [objRes, tgtRes, strRes, hookRes, ctaRes, rubRes,
        tplRes, evtRes, prodRes, profileRes, seqRes, channelRes] =
        await Promise.all([
          api.get('/content-config/objectives'),
          api.get('/content-config/targets'),
          api.get('/content-config/structures'),
          api.get('/content-config/hooks'),
          api.get('/content-config/ctas'),
          api.get('/content/rubriche'),
          api.get('/content-config/templates'),
          api.get('/events?status=open'),
          api.get('/offering/products?status=attivo'),
          api.get('/company/profile'),
          api.get('/planner/sequences?status=active'),
          api.get('/social-profiles'),
        ]);
      setObjectives(objRes.data || []);
      setTargets(tgtRes.data || []);
      setStructures(strRes.data || []);
      setHooks(hookRes.data || []);
      setCtas(ctaRes.data || []);
      setRubriche(rubRes.data || []);
      setTemplates(tplRes.data || []);
      setEvents(evtRes.data || []);
      setProducts(prodRes.data || []);
      const profile = profileRes.data || {};
      setBrandContext(profile);
      setSequences(seqRes.data || []);
      setChannels((channelRes.data || []).filter(c => c.buffer_profile_id));

      // Precompile brand context into form
      setForm(prev => ({
        ...prev,
        tone_override: prev.tone_override || profile.tone_of_voice || '',
        positioning_override: prev.positioning_override || profile.positioning || '',
      }));
    } catch (e) { console.error('Config load error:', e); }
    setLoading(false);
  }, []);

  const loadSavedDraft = useCallback(async () => {
    try {
      const res = await api.get('/approval-queue?status=configuring');
      if (res.data?.length > 0) setSavedDraft(res.data[0]);
    } catch {}
  }, []);

  useEffect(() => { loadConfig(); loadSavedDraft(); }, [loadConfig, loadSavedDraft]);

  // BUG 1.5: Read URL params from Planner/Events navigation
  useEffect(() => {
    const topic = searchParams.get('topic');
    const type = searchParams.get('type');
    const date = searchParams.get('date');
    const eventId = searchParams.get('event');
    const seqId = searchParams.get('sequence');
    const postNum = searchParams.get('post');
    const objective = searchParams.get('objective');
    const contentId = searchParams.get('id');

    let shouldSwitchToCreate = false;

    if (topic) { setForm(prev => ({ ...prev, topic: decodeURIComponent(topic) })); shouldSwitchToCreate = true; }
    if (type === 'newsletter') { setContentType('newsletter'); shouldSwitchToCreate = true; }
    if (date) { setForm(prev => ({ ...prev, scheduled_date: date })); shouldSwitchToCreate = true; }
    if (eventId) { setForm(prev => ({ ...prev, linked_event_id: eventId })); shouldSwitchToCreate = true; }
    if (objective) { setForm(prev => ({ ...prev, objective_id: objective })); shouldSwitchToCreate = true; }
    if (seqId) {
      const seq = sequences.find(s => s.id === seqId);
      if (seq) selectSequence(seq);
      if (postNum) setForm(prev => ({ ...prev, sequence_number: parseInt(postNum) || 1 }));
      shouldSwitchToCreate = true;
    }
    if (contentId) {
      const loadExisting = async () => {
        try {
          const res = await api.get(`/approval-queue/${contentId}`);
          if (res.data) {
            setResult({ content_id: contentId, generated_text: res.data.content, content_score: res.data.content_score, metadata: { status: res.data.status, channel: res.data.channel, rubrica: res.data.rubrica_name, objective: res.data.objective_label } });
          }
        } catch {}
      };
      loadExisting();
      shouldSwitchToCreate = true;
    }

    if (shouldSwitchToCreate) setViewMode('create');
  }, [searchParams, sequences]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Sequence selection ───
  const selectSequence = async (seq) => {
    setActiveSequence(seq);
    try {
      const detail = await api.get(`/planner/sequences/${seq.id}`);
      const d = detail.data || seq;
      const locked = {};
      const updates = {};
      if (d.objective_id) { updates.objective_id = d.objective_id; locked.objective_id = true; }
      if (d.target_ids?.length) { updates.target_ids = d.target_ids; locked.target_ids = true; }
      if (d.rubrica_id) { updates.rubrica_id = d.rubrica_id; locked.rubrica_id = true; }
      if (d.structure_id) { updates.structure_id = d.structure_id; locked.structure_id = true; }
      if (d.linked_event_id) { updates.linked_event_id = d.linked_event_id; locked.linked_event_id = true; }
      if (d.channel) { updates.channels = [d.channel]; locked.channels = true; }
      setLockedFields(locked);
      setForm(prev => ({ ...prev, ...updates, sequence_id: seq.id }));
    } catch { toast.error('Errore caricamento sequenza'); }
  };

  const clearSequence = () => {
    setActiveSequence(null);
    setLockedFields({});
    setForm(prev => ({ ...prev, sequence_id: '', sequence_number: 1 }));
  };

  // ─── Auto-save ───
  useEffect(() => {
    if (!form.topic) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      try {
        if (draftId) {
          await api.patch(`/approval-queue/${draftId}`, {
            content: form.topic, title: `Bozza: ${form.topic.substring(0, 40)}`,
            auto_saved_at: new Date().toISOString(),
          });
        } else {
          const res = await api.post('/approval-queue', {
            channel: form.channels[0] || 'buffer_linkedin_personal',
            content: form.topic,
            title: `Bozza: ${form.topic.substring(0, 40)}`,
            status: 'configuring', source: 'content_studio',
            auto_saved_at: new Date().toISOString(),
          });
          setDraftId(res.data?.id);
        }
      } catch {}
    }, 30000);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [form.topic, form.channels, draftId]);

  // ─── Scout reports ───
  const openScoutSheet = async () => {
    setScoutOpen(true);
    try {
      const res = await api.get('/scout/reports?limit=3');
      setScoutReports(res.data || []);
    } catch { setScoutReports([]); }
  };

  // ─── Image library ───
  const openImageSheet = async () => {
    setImageSheetOpen(true);
    try {
      const res = await api.get(`/designer/assets?type=image&folder=${imageFolder}&limit=20`);
      setImageAssets(res.data || []);
    } catch { setImageAssets([]); }
  };

  // ─── Generate ───
  const generate = async (overrides = {}) => {
    if (!form.topic.trim()) { toast.error('Inserisci un tema'); return; }
    if (!form.objective_id && !overrides.skipValidation) { toast.error('Seleziona un obiettivo'); return; }
    if (contentType === 'post' && !form.channels.length && !overrides.skipValidation) { toast.error('Seleziona almeno un canale per generare'); return; }
    setGenerating(true);
    setResult(null);
    setGenStep(1);
    const stepTimer1 = setTimeout(() => setGenStep(2), 800);
    const stepTimer2 = setTimeout(() => setGenStep(3), 1800);
    const stepTimer3 = setTimeout(() => setGenStep(4), 2800);
    try {
      const payload = {
        content_type: 'post',
        topic: form.topic,
        objective_id: form.objective_id || undefined,
        target_ids: form.target_ids.length ? form.target_ids : undefined,
        rubrica_id: form.rubrica_id || undefined,
        structure_id: form.structure_id || undefined,
        hook_type_id: form.hook_type_id || undefined,
        cta_type_id: form.cta_type_id || undefined,
        length: form.length,
        language: form.language,
        emoji_level: form.emoji_level,
        channels: form.channels.length ? form.channels : ['buffer_linkedin_personal'],
        scheduled_date: form.scheduled_date || undefined,
        scheduled_time: form.scheduled_time || undefined,
        linked_event_id: form.linked_event_id || undefined,
        linked_product_id: form.linked_product_id || undefined,
        inspiration_source: form.inspiration_source || undefined,
        cite_sources: form.cite_sources.length ? form.cite_sources : undefined,
        tagged_people: form.tagged_people.length ? form.tagged_people : undefined,
        hashtags: form.hashtags.length ? form.hashtags : undefined,
        tone_override: form.tone_override || undefined,
        positioning_override: form.positioning_override || undefined,
        keywords_add: form.keywords_add.length ? form.keywords_add : undefined,
        keywords_avoid_add: form.keywords_avoid_add.length ? form.keywords_avoid_add : undefined,
        memory_flags: form.memory_flags,
        sequence_id: form.sequence_id || undefined,
        sequence_number: form.sequence_number || undefined,
        ...overrides,
      };
      const res = await api.post('/content/generate', payload);
      setResult(res.data);
      if (draftId) {
        await api.patch(`/approval-queue/${draftId}`, { status: 'draft' });
        setDraftId(null);
      }
      toast.success('Contenuto generato!');
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (detail.toLowerCase().includes('buffer')) {
        toast.error('Errore pubblicazione — verifica il collegamento Buffer nelle Impostazioni');
      } else if (detail.toLowerCase().includes('beehiiv')) {
        toast.error('Errore newsletter — verifica la configurazione Beehiiv');
      } else {
        toast.error(detail || 'Errore nella generazione. Riprova o contatta il supporto.');
      }
    } finally { setGenerating(false); setGenStep(0); clearTimeout(stepTimer1); clearTimeout(stepTimer2); clearTimeout(stepTimer3); }
  };

  // ─── Actions ───
  const approveContent = async () => {
    if (!result?.content_id) return;
    try {
      const res = await api.patch(`/approval-queue/${result.content_id}`, { action: 'approved' });
      const data = res.data;

      if (activeSequence) {
        try {
          await api.patch(`/planner/sequences/${activeSequence.id}/items/${form.sequence_number}`, {
            status: 'approved', content_id: result.content_id,
          });
        } catch {}
      }

      const undoAction = {
        label: 'Annulla',
        onClick: async () => {
          try {
            await api.patch(`/approval-queue/${result.content_id}`, { action: 'rejected', rejection_note: 'Annullato dall\'utente' });
            setResult(prev => prev ? { ...prev, metadata: { ...prev.metadata, status: 'draft' } } : null);
            toast.success('Approvazione annullata — riportato in bozza');
          } catch {}
        },
      };

      if (data.status === 'sent') {
        toast.success(`Pubblicato su ${data.detail || 'canale'}!`, { duration: 5000 });
      } else if (data.status === 'rescheduled') {
        const when = data.new_time ? new Date(data.new_time).toLocaleString('it-CH', {
          weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
        }) : 'prossimo slot';
        toast.success(`Approvato — programmato per ${when}`, {
          description: data.reason || 'Fuori dalla finestra di invio',
          action: undoAction, duration: 5000,
        });
      } else if (data.status === 'scheduled') {
        toast.warning('Approvato ma invio fallito — riprogrammato', {
          description: data.error || 'Il sistema riproverà automaticamente',
          action: undoAction, duration: 5000,
        });
      } else {
        toast.success('Contenuto approvato', { action: undoAction, duration: 5000 });
      }

      setResult(prev => prev ? { ...prev, metadata: { ...prev.metadata, status: data.status || 'approved' } } : null);
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (detail.toLowerCase().includes('buffer')) {
        toast.error('Errore pubblicazione — verifica il collegamento Buffer nelle Impostazioni');
      } else if (detail.toLowerCase().includes('beehiiv')) {
        toast.error('Errore invio newsletter — verifica la configurazione Beehiiv');
      } else {
        toast.error(detail || 'Errore approvazione. Riprova o contatta il supporto.');
      }
    }
  };

  const rejectContent = async () => {
    if (!result?.content_id || !rejectNote.trim()) { toast.error('Inserisci il motivo del rifiuto'); return; }
    try {
      await api.patch(`/approval-queue/${result.content_id}`, { action: 'rejected', rejection_note: rejectNote });
      toast.success('Contenuto rifiutato');
      setResult(prev => prev ? { ...prev, metadata: { ...prev.metadata, status: 'rejected' } } : null);
      setRejectOpen(false);
      setRejectNote('');
    } catch { toast.error('Errore rifiuto'); }
  };

  const regenerate = async () => {
    setRegenOpen(false);
    await generate({ regenerate_note: regenNote || undefined });
    setRegenNote('');
  };

  const saveEdit = async () => {
    if (!result?.content_id) return;
    try {
      await api.patch(`/approval-queue/${result.content_id}`, {
        content: editText, was_modified: true, original_content: result.generated_text,
      });
      setResult(prev => ({ ...prev, generated_text: editText }));
      setEditing(false);
      toast.success('Modifiche salvate');
    } catch { toast.error('Errore salvataggio'); }
  };

  const duplicate = async (targetChannel) => {
    if (!result?.content_id) return;
    setDuplicateOpen(false);
    setGenerating(true);
    try {
      const res = await api.post('/content/generate', {
        content_type: 'post_adaptation',
        source_content_id: result.content_id,
        target_channel: targetChannel,
        adaptation_note: `Adatta per ${targetChannel}`,
      });
      setResult(res.data);
      toast.success(`Contenuto adattato per ${targetChannel}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore adattamento');
    } finally { setGenerating(false); }
  };

  // ─── Template ───
  const applyTemplate = (tpl) => {
    const cfg = tpl.config || {};
    setForm(prev => ({
      ...prev,
      objective_id: cfg.objective_id || prev.objective_id,
      target_ids: cfg.target_ids || prev.target_ids,
      rubrica_id: cfg.rubrica_id || prev.rubrica_id,
      structure_id: cfg.structure_id || prev.structure_id,
      hook_type_id: cfg.hook_type_id || prev.hook_type_id,
      cta_type_id: cfg.cta_type_id || prev.cta_type_id,
      length: cfg.length || prev.length,
      language: cfg.language || prev.language,
      emoji_level: cfg.emoji_level ?? prev.emoji_level,
      channels: cfg.channels || prev.channels,
      hashtags: cfg.hashtags || prev.hashtags,
    }));
    toast.success(`Template "${tpl.name}" applicato`);
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) return;
    try {
      await api.post('/content-config/templates', {
        name: templateName.trim(), content_type: 'post',
        config: {
          objective_id: form.objective_id, target_ids: form.target_ids,
          rubrica_id: form.rubrica_id, structure_id: form.structure_id,
          hook_type_id: form.hook_type_id, cta_type_id: form.cta_type_id,
          length: form.length, language: form.language,
          emoji_level: form.emoji_level, channels: form.channels,
          hashtags: form.hashtags,
        },
      });
      toast.success('Template salvato');
      setShowSaveTemplate(false);
      setTemplateName('');
      loadConfig();
    } catch { toast.error('Errore nel salvataggio template'); }
  };

  // BUG 1.2: Content list loading
  const loadContentList = useCallback(async () => {
    setListLoading(true);
    try {
      const statusParam = listFilter === 'all' ? '' : `&status=${listFilter}`;
      const res = await api.get(`/approval-queue?source=content_studio${statusParam}`);
      setContentList(res.data || []);
    } catch { setContentList([]); }
    setListLoading(false);
  }, [listFilter]);

  useEffect(() => { if (viewMode === 'list') loadContentList(); }, [viewMode, loadContentList]);

  // BUG 1.3: Image upload handler
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/designer/assets/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.url || res.data?.secure_url) {
        setSelectedImage({ url: res.data.url || res.data.secure_url, name: file.name });
        toast.success('Immagine caricata');
      }
    } catch { toast.error('Errore caricamento immagine'); }
  };

  const resumeDraft = () => {
    if (!savedDraft) return;
    const gp = savedDraft.generation_params || {};
    setForm(prev => ({
      ...prev, topic: savedDraft.content || gp.topic || '',
      channels: gp.channels || (savedDraft.channel ? [savedDraft.channel] : prev.channels),
      objective_id: gp.objective_id || '', target_ids: gp.target_ids || [],
      rubrica_id: savedDraft.rubrica_id || gp.rubrica_id || '',
      structure_id: gp.structure_id || '', hook_type_id: gp.hook_type_id || '',
      cta_type_id: gp.cta_type_id || '', length: gp.length || 'medium',
      language: savedDraft.language || gp.language || 'it',
    }));
    setDraftId(savedDraft.id);
    setSavedDraft(null);
    toast.success('Bozza ripristinata');
  };

  const score = result?.content_score;
  const scoreColor = score ? getScoreColor(score.grade) : '#94A3B8';
  const selectedHook = hooks.find(h => h.id === form.hook_type_id);
  const selectedCta = ctas.find(c => c.id === form.cta_type_id);
  const brandKeywords = brandContext?.keywords_use || [];
  const brandAvoid = brandContext?.keywords_avoid || [];

  // Completion counts
  const contestoFilled = [form.objective_id, form.target_ids.length, form.topic].filter(Boolean).length;
  const composizioneFilled = [form.structure_id, form.hook_type_id, form.cta_type_id].filter(Boolean).length;
  const distribuzioneFilled = [form.channels?.length, form.scheduled_date].filter(Boolean).length;
  const brandFilled = [form.tone_override, form.positioning_override].filter(Boolean).length;

  // ProcessBar step
  const processStep = result ? (result.metadata?.status === 'approved' ? 3 : 2) : 1;

  // Selected rubrica for tooltip
  const selectedRubrica = rubriche.find(r => r.id === form.rubrica_id);

  // Gear icon helper
  const GearLabel = ({ label, configType: ct }) => (
    <div className="flex items-center justify-between mb-1">
      <label className="text-[11px] font-medium" style={{ color: 'var(--color-muted)' }}>{label}</label>
      <button onClick={() => setConfigDialogType(ct)}
        className="text-[10px] flex items-center gap-1 transition-colors"
        style={{ color: 'var(--color-muted)' }}
        onMouseEnter={e => e.currentTarget.style.color = '#2563EB'}
        onMouseLeave={e => e.currentTarget.style.color = 'var(--color-muted)'}
        data-testid={`gear-${ct}`}>
        &#9881; modifica
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center" data-testid="content-studio-loading">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-warm-accent)' }} />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="h-full flex flex-col gap-3" data-testid="content-studio-page">
        {/* ─── HEADER ─── */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold" style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-display)' }}>
              Content Studio
            </h1>
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
              <button onClick={() => setViewMode('create')}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${viewMode === 'create' ? 'bg-white shadow-sm' : 'text-muted-foreground hover:text-gray-700'}`}
                data-testid="view-create">
                <Plus className="w-3 h-3 inline mr-1" /> Crea
              </button>
              <button onClick={() => setViewMode('list')}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm' : 'text-muted-foreground hover:text-gray-700'}`}
                data-testid="view-list">
                <List className="w-3 h-3 inline mr-1" /> Contenuti
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {templates.length > 0 && (
              <Select onValueChange={v => { const tpl = templates.find(t => t.id === v); if (tpl) applyTemplate(tpl); }}>
                <SelectTrigger className="text-xs h-8 w-[160px]" data-testid="template-select">
                  <SelectValue placeholder="Template..." />
                </SelectTrigger>
                <SelectContent>{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {result && (
              <Button variant="outline" size="sm" onClick={() => setShowSaveTemplate(true)} data-testid="save-template-btn">
                <Save className="w-3.5 h-3.5 mr-1.5" /> Salva template
              </Button>
            )}
          </div>
        </div>

        {/* ─── CONTENT LIST VIEW (BUG 1.2) ─── */}
        {viewMode === 'list' && (
          <div className="flex-1 overflow-y-auto space-y-3" data-testid="content-list-view">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 flex-wrap">
                {['all', 'draft', 'approved', 'rejected', 'configuring'].map(f => (
                  <Badge key={f} variant={listFilter === f ? 'default' : 'outline'}
                    className="cursor-pointer text-[11px] px-2 py-0.5"
                    onClick={() => setListFilter(f)}
                    data-testid={`filter-${f}`}>
                    {f === 'all' ? 'Tutti' : f === 'draft' ? 'Bozze' : f === 'approved' ? 'Approvati' : f === 'rejected' ? 'Rifiutati' : 'In corso'}
                  </Badge>
                ))}
              </div>
            </div>
            {listLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>
            ) : contentList.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <FileText className="w-10 h-10 text-gray-300 mb-3" />
                <p className="text-sm font-medium" style={{ color: 'var(--color-primary)' }}>Nessun contenuto</p>
                <p className="text-xs mt-1" style={{ color: 'var(--color-muted)' }}>Crea il tuo primo contenuto con il tab "Crea"</p>
              </div>
            ) : (
              <div className="space-y-2">
                {contentList.map(item => (
                  <Card key={item.id} className="p-3 cursor-pointer hover:bg-gray-50/50 transition-colors"
                    style={{ boxShadow: 'var(--shadow-card)', borderRadius: 'var(--radius-soft)', border: 'none' }}
                    onClick={() => { setViewMode('create'); setResult({ content_id: item.id, generated_text: item.content, content_score: item.content_score, metadata: { status: item.status, channel: item.channel, rubrica: item.rubrica_name, objective: item.objective_label } }); }}
                    data-testid={`content-item-${item.id}`}>
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-xs font-semibold truncate" style={{ color: 'var(--color-primary)' }}>
                            {item.title || item.content?.substring(0, 60) || 'Contenuto'}
                          </p>
                          <Badge variant="outline" className="text-[10px] flex-shrink-0" style={{
                            color: item.status === 'approved' ? '#059669' : item.status === 'rejected' ? '#DC2626' : item.status === 'sent' ? '#059669' : item.status === 'scheduled' ? '#7C3AED' : '#D97706',
                            borderColor: item.status === 'approved' ? '#BBF7D0' : item.status === 'rejected' ? '#FECACA' : item.status === 'sent' ? '#BBF7D0' : item.status === 'scheduled' ? '#DDD6FE' : '#FDE68A',
                          }}>
                            {item.status === 'approved' ? 'Approvato' : item.status === 'rejected' ? 'Rifiutato' : item.status === 'draft' ? 'Bozza' : item.status === 'sent' ? 'Pubblicato' : item.status === 'scheduled' ? 'Programmato' : item.status}
                          </Badge>
                        </div>
                        <p className="text-[11px] line-clamp-2" style={{ color: '#4B5563' }}>
                          {item.content?.substring(0, 120)}
                        </p>
                        <div className="flex items-center gap-2 mt-1.5 text-[10px]" style={{ color: 'var(--color-muted)' }}>
                          {item.channel && <span>{item.channel}</span>}
                          {item.generated_at && <span>{new Date(item.generated_at).toLocaleDateString('it-CH')}</span>}
                          {item.content_score?.total && <span>Score: {item.content_score.total}/100</span>}
                        </div>
                      </div>
                      {(item.status === 'draft' || item.status === 'configuring' || item.status === 'rejected') && (
                        <button onClick={async (e) => {
                          e.stopPropagation();
                          if (!window.confirm('Eliminare questo contenuto?')) return;
                          try {
                            await api.patch(`/approval-queue/${item.id}`, { action: 'rejected', rejection_note: 'Eliminato dall\'utente' });
                            toast.success('Contenuto eliminato');
                            loadContentList();
                          } catch { toast.error('Errore eliminazione'); }
                        }} className="text-gray-400 hover:text-red-500 transition-colors p-1 flex-shrink-0" data-testid={`delete-${item.id}`}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─── CREATE VIEW ─── */}
        {viewMode === 'create' && (<>
        {/* ─── DRAFT BANNER ─── */}
        {savedDraft && (
          <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'var(--color-warm-accent-light)', border: '1px solid var(--color-warm-accent)' }} data-testid="saved-draft-banner">
            <FileText size={16} style={{ color: 'var(--color-warm-accent)' }} />
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium block truncate">Bozza: "<strong>{savedDraft.title || savedDraft.content?.substring(0, 50) || 'Senza titolo'}</strong>"</span>
              <span className="text-[11px] block" style={{ color: 'var(--color-muted)' }}>
                {savedDraft.rubrica_id && rubriche.find(r => r.id === savedDraft.rubrica_id)?.code ? `Rubrica: ${rubriche.find(r => r.id === savedDraft.rubrica_id).code}` : ''}
                {savedDraft.generation_params?.objective_id && objectives.find(o => o.id === savedDraft.generation_params.objective_id)?.label ? ` · Obiettivo: ${objectives.find(o => o.id === savedDraft.generation_params.objective_id).label}` : ''}
                {savedDraft.channel ? ` · ${savedDraft.channel}` : ''}
                {' · Salvata '}{new Date(savedDraft.auto_saved_at || savedDraft.updated_at || savedDraft.created_at).toLocaleString('it-CH', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            <Button size="sm" variant="outline" onClick={resumeDraft} data-testid="resume-draft-btn">Riprendi</Button>
            <Button size="sm" variant="ghost" onClick={async () => { try { await api.patch(`/approval-queue/${savedDraft.id}`, { action: 'rejected' }); setSavedDraft(null); toast.success('Bozza eliminata'); } catch {} }}>
              <Trash2 size={14} />
            </Button>
          </div>
        )}

        {/* ─── CONTEXT BANNER (V6.3) ─── */}
        {(searchParams.get('event') || searchParams.get('sequence')) && (
          <div className="flex items-center gap-2 p-2.5 rounded-lg" style={{ background: '#F0F9FF', border: '1px solid #BAE6FD' }} data-testid="context-banner">
            <ArrowLeft className="w-4 h-4 text-blue-500 flex-shrink-0" />
            <span className="text-xs font-medium text-blue-800 flex-1">
              {searchParams.get('event') && `Stai creando un contenuto per promuovere un evento`}
              {searchParams.get('sequence') && `Stai lavorando sulla sequenza — Post ${searchParams.get('post') || '?'}`}
            </span>
            <button onClick={() => window.history.back()} className="text-[11px] text-blue-600 hover:underline">Torna indietro</button>
          </div>
        )}

        {/* ─── SEQUENCE BANNER ─── */}
        {activeSequence && (
          <div className="flex items-center gap-2 p-2.5 rounded-lg bg-blue-50 border border-blue-200" data-testid="sequence-banner">
            <Lock className="w-4 h-4 text-blue-600" />
            <span className="text-xs font-medium text-blue-800 flex-1">
              Sequenza: {activeSequence.name || activeSequence.title} — Post {form.sequence_number}/{activeSequence.total_posts || '?'}
            </span>
            <Button size="sm" variant="ghost" className="h-6 text-xs text-blue-600" onClick={clearSequence}>Sblocca</Button>
          </div>
        )}

        {/* ─── MAIN 2-COL LAYOUT ─── */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-3 min-h-0 overflow-hidden">

          {/* ═══ LEFT PANEL ═══ */}
          <div className="overflow-y-auto space-y-3 pr-1" style={{ maxWidth: 420, flexShrink: 0 }} data-testid="variable-panel">

            {/* ProcessBar */}
            <ProcessBar steps={['Configura', 'Genera', 'Pubblica']} step={processStep} />

            {/* Campo 1: Tipo contenuto */}
            <div className="grid grid-cols-3 gap-2 pb-2">
              {[
                { key: 'post', label: 'Post', active: true },
                { key: 'newsletter', label: 'Newsletter', active: false },
                { key: 'idea', label: 'Idea', active: true },
              ].map(t => (
                <Tooltip key={t.key}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => t.active && setContentType(t.key)}
                      className={`py-3 rounded-lg text-xs font-semibold border-2 transition-all ${contentType === t.key ? 'border-blue-500 bg-blue-50 text-blue-700' : t.active ? 'border-gray-200 hover:border-gray-300 bg-white' : 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed'}`}
                      disabled={!t.active}
                      data-testid={`content-type-${t.key}`}>
                      {t.label}
                    </button>
                  </TooltipTrigger>
                  {!t.active && (
                    <TooltipContent>
                      <p className="text-xs">Editor Newsletter in arrivo con Fase 3</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              ))}
            </div>

            {/* ─── IDEA QUICK FORM (V7.1) ─── */}
            {contentType === 'idea' && (
              <Card style={{ boxShadow: 'var(--shadow-card)', borderRadius: 'var(--radius-soft)', border: 'none' }}>
                <CardContent className="p-4 space-y-3">
                  <p className="text-xs font-semibold" style={{ color: 'var(--color-primary)' }}>Cattura idea rapida</p>
                  <Input placeholder="Titolo dell'idea" value={form.topic}
                    onChange={e => setForm(prev => ({ ...prev, topic: e.target.value }))}
                    className="text-xs" data-testid="idea-title" />
                  <Textarea placeholder="Note, spunti, link..." value={form.inspiration_source}
                    onChange={e => setForm(prev => ({ ...prev, inspiration_source: e.target.value }))}
                    className="text-xs min-h-[80px]" data-testid="idea-notes" />
                  <Button className="w-full" size="sm" disabled={!form.topic.trim()} data-testid="save-idea-btn"
                    onClick={async () => {
                      try {
                        await api.post('/content/generate', {
                          content_type: 'idea', topic: form.topic,
                          inspiration_source: form.inspiration_source || undefined,
                          channels: ['buffer_linkedin_personal'],
                          language: form.language,
                        });
                        toast.success('Idea salvata!');
                        setForm(prev => ({ ...prev, topic: '', inspiration_source: '' }));
                        loadContentList();
                      } catch (e) { toast.error(e.response?.data?.detail || 'Errore salvataggio idea'); }
                    }}>
                    <Save className="w-3.5 h-3.5 mr-1.5" />Salva idea
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* ─── SEZIONE 1: CONTESTO ─── */}
            <Card style={{ boxShadow: 'var(--shadow-card)', borderRadius: 'var(--radius-soft)', border: 'none' }}>
              <FormSection title="Contesto" defaultOpen={true} filled={contestoFilled} total={3}>
                {/* Campo 2: Sequenza dal Planner */}
                {sequences.length > 0 && !activeSequence && (
                  <div>
                    <label className="text-[11px] font-medium block mb-1.5" style={{ color: 'var(--color-muted)' }}>Sequenze attive dal Planner</label>
                    <div className="space-y-1.5">
                      {sequences.map(seq => (
                        <button key={seq.id} onClick={() => selectSequence(seq)}
                          className="w-full text-left p-2.5 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50/50 transition-all"
                          data-testid={`sequence-${seq.id}`}>
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium">{seq.name || seq.title}</span>
                            <span className="text-[10px] text-muted-foreground">{seq.current_post || 1}/{seq.total_posts || '?'}</span>
                          </div>
                          <div className="text-[10px] text-muted-foreground mt-0.5">
                            {seq.objective_label || seq.objective || ''} {seq.target_labels?.join(', ') || ''}
                          </div>
                          <div className="flex items-center gap-1 mt-1 text-[10px] text-blue-600">
                            <Lock className="w-3 h-3" /> Variabili definite dal Planner
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Campo 3: Obiettivo */}
                <div>
                  <GearLabel label={`Obiettivo ${lockedFields.objective_id ? '' : ''}`} configType="objectives" />
                  {lockedFields.objective_id && <Lock className="w-3 h-3 inline ml-1 text-muted-foreground" />}
                  {objectives.length === 0 ? (
                    <p className="text-[11px] text-muted-foreground italic">Nessun obiettivo configurato — configura nelle impostazioni</p>
                  ) : (
                    <Select value={form.objective_id} onValueChange={v => { updateForm('objective_id', v); setDismissedSuggestion(false); }} disabled={!!lockedFields.objective_id}>
                      <SelectTrigger className="text-xs h-9" data-testid="objective-select">
                        <SelectValue placeholder="Seleziona obiettivo" />
                      </SelectTrigger>
                      <SelectContent>
                        {objectives.map(o => (
                          <SelectItem key={o.id} value={o.id}>
                            <div>
                              <div className="text-xs font-medium">{o.label}</div>
                              {o.description && <div className="text-[10px]" style={{ color: 'var(--color-muted)' }}>{o.description}</div>}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {/* Contextual suggestion */}
                  {form.objective_id && !dismissedSuggestion && hooks.length > 0 && ctas.length > 0 && (
                    <div className="mt-1.5 p-2 rounded-lg flex items-start gap-2"
                      style={{ background: '#FFFBF7', border: '1px solid #FDE68A' }} data-testid="objective-suggestion">
                      <span className="text-[11px] flex-shrink-0 mt-0.5">&#128161;</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px]" style={{ color: '#92400E' }}>
                          Suggerimento: prova con Hook "<strong>{hooks[0]?.label}</strong>" e CTA "<strong>{ctas[0]?.label}</strong>"
                        </p>
                        <div className="flex gap-2 mt-1">
                          <button onClick={() => {
                            updateForm('hook_type_id', hooks[0]?.id);
                            updateForm('cta_type_id', ctas[0]?.id);
                            setDismissedSuggestion(true);
                          }} className="text-[10px] font-medium" style={{ color: '#2563EB' }}>Applica</button>
                          <button onClick={() => setDismissedSuggestion(true)} className="text-[10px]" style={{ color: '#92400E' }}>&#10005;</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Campo 4: Target */}
                <div>
                  <GearLabel label={`Target / Audience ${lockedFields.target_ids ? '' : ''}`} configType="targets" />
                  {lockedFields.target_ids && <Lock className="w-3 h-3 inline text-muted-foreground" />}
                  <div className="flex flex-wrap gap-1.5">
                    {targets.map(t => (
                      <Tooltip key={t.id}>
                        <TooltipTrigger asChild>
                          <Badge variant={form.target_ids.includes(t.id) ? 'default' : 'outline'}
                            className={`cursor-pointer text-[11px] px-2 py-0.5 ${lockedFields.target_ids ? 'opacity-60 cursor-not-allowed' : ''}`}
                            onClick={() => !lockedFields.target_ids && updateForm('target_ids', form.target_ids.includes(t.id) ? form.target_ids.filter(x => x !== t.id) : [...form.target_ids, t.id])}
                            data-testid={`target-${t.id}`}>
                            {t.label}
                          </Badge>
                        </TooltipTrigger>
                        {t.description && <TooltipContent><p className="text-xs max-w-[200px]">{t.description}</p></TooltipContent>}
                      </Tooltip>
                    ))}
                  </div>
                </div>

                {/* Campo 5: Tema + Deep Research link */}
                <div>
                  <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>Tema / Argomento</label>
                  <Textarea placeholder="Scrivi il tema del contenuto..." value={form.topic}
                    onChange={e => updateForm('topic', e.target.value)} rows={3}
                    className="text-sm" data-testid="topic-input" />
                  <button onClick={openScoutSheet} className="text-[11px] text-blue-600 hover:underline mt-1 flex items-center gap-1" data-testid="scout-link">
                    <Search className="w-3 h-3" /> Cerca con Deep Research
                  </button>
                </div>

                {/* Campo 6: Fonte ispirazione */}
                <div>
                  <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>Fonte di ispirazione</label>
                  <Input placeholder="URL articolo, nota da call, insight..."
                    value={form.inspiration_source}
                    onChange={e => updateForm('inspiration_source', e.target.value)}
                    className="text-xs h-8" data-testid="inspiration-input" />
                </div>

                {/* Campo 7: Fonti da citare */}
                <div>
                  <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>Fonti da citare nel post</label>
                  <TagInput tags={form.cite_sources} onChange={v => updateForm('cite_sources', v)}
                    placeholder="Digita e premi Enter..." pillColor="#6366F1" />
                </div>

                {/* Campo 8: Rubrica */}
                <div>
                  <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>
                    Rubrica {lockedFields.rubrica_id && <Lock className="w-3 h-3 inline ml-1" />}
                  </label>
                  <Select value={form.rubrica_id} onValueChange={v => updateForm('rubrica_id', v)} disabled={!!lockedFields.rubrica_id}>
                    <SelectTrigger className="text-xs h-9" data-testid="rubrica-select"><SelectValue placeholder="Rubrica" /></SelectTrigger>
                    <SelectContent>
                      {rubriche.map(r => (
                        <SelectItem key={r.id} value={r.id}>
                          <div>
                            <div className="text-xs font-medium">{r.code ? `${r.code} - ` : ''}{r.name}</div>
                            {(r.detailed_description || r.description) && <div className="text-[10px] text-muted-foreground">{r.detailed_description || r.description}</div>}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedRubrica && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <p className="text-[10px] mt-1 cursor-help truncate" style={{ color: '#6366F1' }}>
                          {selectedRubrica.code ? `${selectedRubrica.code}: ` : ''}{selectedRubrica.name} &mdash; {selectedRubrica.detailed_description || selectedRubrica.description || ''}
                        </p>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[280px]">
                        <p className="text-xs">{selectedRubrica.detailed_description || selectedRubrica.description || selectedRubrica.name}</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>

                {/* Campo 9: Sequenza libera */}
                {!activeSequence && (
                  <div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <label className="text-[11px] font-medium" style={{ color: 'var(--color-muted)' }}>Sequenza libera</label>
                      <button onClick={() => updateForm('free_sequence', !form.free_sequence)}
                        className={`w-9 h-5 rounded-full transition-colors relative ${form.free_sequence ? 'bg-blue-500' : 'bg-gray-300'}`}
                        data-testid="free-sequence-toggle">
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.free_sequence ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                    {form.free_sequence && (
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] text-muted-foreground">Quanti post?</label>
                          <Input type="number" min={1} max={10} value={form.free_sequence_count}
                            onChange={e => updateForm('free_sequence_count', parseInt(e.target.value) || 1)}
                            className="text-xs h-7" data-testid="free-seq-count" />
                        </div>
                        <div>
                          <label className="text-[10px] text-muted-foreground">Quale post?</label>
                          <Input type="number" min={1} max={form.free_sequence_count} value={form.sequence_number}
                            onChange={e => updateForm('sequence_number', parseInt(e.target.value) || 1)}
                            className="text-xs h-7" data-testid="free-seq-number" />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Campo 10: Evento collegato */}
                {events.length > 0 && (
                  <div>
                    <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>
                      Evento collegato {lockedFields.linked_event_id && <Lock className="w-3 h-3 inline ml-1" />}
                    </label>
                    <Select value={form.linked_event_id} onValueChange={v => updateForm('linked_event_id', v)} disabled={!!lockedFields.linked_event_id}>
                      <SelectTrigger className="text-xs h-9" data-testid="event-select"><SelectValue placeholder="Collega evento" /></SelectTrigger>
                      <SelectContent>
                        {events.map(e => (
                          <SelectItem key={e.id} value={e.id}>
                            <div>
                              <div className="text-xs font-medium">{e.name}</div>
                              <div className="text-[10px] text-muted-foreground">
                                {e.date ? new Date(e.date).toLocaleDateString('it-CH') : ''} {e.location ? `· ${e.location}` : ''}
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Campo 11: Prodotto collegato */}
                <div>
                  <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>Prodotto collegato</label>
                  {products.length === 0 ? (
                    <p className="text-[11px] text-muted-foreground italic">Nessun prodotto attivo</p>
                  ) : (
                    <Select value={form.linked_product_id} onValueChange={v => updateForm('linked_product_id', v)}>
                      <SelectTrigger className="text-xs h-9" data-testid="product-select"><SelectValue placeholder="Collega prodotto" /></SelectTrigger>
                      <SelectContent>
                        {products.map(p => (
                          <SelectItem key={p.id} value={p.id}>
                            <div>
                              <div className="text-xs font-medium">{p.name}</div>
                              <div className="text-[10px] text-muted-foreground">
                                {p.price ? `${p.price}` : ''} {p.description ? `· ${p.description.substring(0, 50)}` : ''}
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* Campo 12: Persone da taggare */}
                <div>
                  <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>Persone da taggare</label>
                  <ContactAutocomplete tags={form.tagged_people} onChange={v => updateForm('tagged_people', v)} />
                </div>
              </FormSection>
            </Card>

            {/* ─── SEZIONE 2: COMPOSIZIONE ─── */}
            <Card style={{ boxShadow: 'var(--shadow-card)', borderRadius: 'var(--radius-soft)', border: 'none' }}>
              <FormSection title="Composizione" filled={composizioneFilled} total={3}>
                {/* Campo 13: Struttura testo */}
                <div>
                  <GearLabel label={`Struttura testo ${lockedFields.structure_id ? '' : ''}`} configType="structures" />
                  <Select value={form.structure_id} onValueChange={v => updateForm('structure_id', v)} disabled={!!lockedFields.structure_id}>
                    <SelectTrigger className="text-xs h-9" data-testid="structure-select"><SelectValue placeholder="Struttura" /></SelectTrigger>
                    <SelectContent>
                      {structures.map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          <div>
                            <div className="text-xs font-medium">{s.label}</div>
                            {s.description && <div className="text-[10px] text-muted-foreground">{s.description}</div>}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Campo 14: Tipo hook */}
                <div>
                  <GearLabel label="Tipo Hook" configType="hooks" />
                  <Select value={form.hook_type_id} onValueChange={v => updateForm('hook_type_id', v)}>
                    <SelectTrigger className="text-xs h-9" data-testid="hook-select"><SelectValue placeholder="Tipo Hook" /></SelectTrigger>
                    <SelectContent>
                      {hooks.map(h => (
                        <SelectItem key={h.id} value={h.id}>
                          <div>
                            <div className="text-xs font-medium">{h.label}</div>
                            {h.example && <div className="text-[10px] text-muted-foreground italic">{h.example}</div>}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedHook?.example && <p className="text-[10px] italic text-muted-foreground mt-1">{selectedHook.example}</p>}
                </div>

                {/* Campo 15: Tipo CTA */}
                <div>
                  <GearLabel label="Tipo CTA" configType="ctas" />
                  <Select value={form.cta_type_id} onValueChange={v => updateForm('cta_type_id', v)}>
                    <SelectTrigger className="text-xs h-9" data-testid="cta-select"><SelectValue placeholder="Tipo CTA" /></SelectTrigger>
                    <SelectContent>
                      {ctas.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          <div>
                            <div className="text-xs font-medium">{c.label}</div>
                            {c.example && <div className="text-[10px] text-muted-foreground italic">{c.example}</div>}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedCta?.example && <p className="text-[10px] italic text-muted-foreground mt-1">{selectedCta.example}</p>}
                </div>

                {/* Campo 16: Lunghezza */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>Lunghezza</label>
                    <Select value={form.length} onValueChange={v => updateForm('length', v)}>
                      <SelectTrigger className="text-xs h-8" data-testid="length-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{LENGTHS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {/* Campo 17: Lingua */}
                  <div>
                    <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>Lingua</label>
                    <Select value={form.language} onValueChange={v => updateForm('language', v)}>
                      <SelectTrigger className="text-xs h-8" data-testid="language-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{LANGUAGES.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Campo 18: Emoji */}
                <div>
                  <label className="text-[11px] font-medium block mb-1.5" style={{ color: 'var(--color-muted)' }}>Emoji</label>
                  <div className="grid grid-cols-3 gap-1.5">
                    {[{ v: 0, l: 'Nessuna' }, { v: 1, l: '1-2' }, { v: 2, l: 'Separatori' }].map(opt => (
                      <button key={opt.v} onClick={() => updateForm('emoji_level', opt.v)}
                        className={`py-1.5 rounded-md text-[11px] font-medium border transition-colors ${form.emoji_level === opt.v ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'}`}
                        data-testid={`emoji-${opt.v}`}>{opt.l}</button>
                    ))}
                  </div>
                </div>

                {/* Campo 19: Hashtag */}
                <div>
                  <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>Hashtag</label>
                  <TagInput tags={form.hashtags} onChange={v => updateForm('hashtags', v)}
                    placeholder="Aggiungi hashtag..." pillColor="#3B82F6" suggestions={['AI', 'coaching', 'humanAImpact', 'leadership', 'innovazione']} />
                </div>
              </FormSection>
            </Card>

            {/* ─── SEZIONE 3: DISTRIBUZIONE ─── */}
            <Card style={{ boxShadow: 'var(--shadow-card)', borderRadius: 'var(--radius-soft)', border: 'none' }}>
              <FormSection title="Distribuzione" filled={distribuzioneFilled} total={2}>
                {/* Campo 20: Canale / Profilo */}
                <div>
                  <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>
                    Canale / Profilo {lockedFields.channels && <Lock className="w-3 h-3 inline ml-1" />}
                  </label>
                  {channels.length === 0 ? (
                    <p className="text-[11px] text-muted-foreground italic">
                      Nessun profilo Buffer collegato — <button onClick={() => navigate('/settings')} className="text-blue-600 hover:underline">vai alle Impostazioni</button>
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {channels.map(ch => {
                        const selected = form.channels.includes(ch.buffer_profile_id || ch.id);
                        return (
                          <Badge key={ch.id} variant={selected ? 'default' : 'outline'}
                            className={`cursor-pointer text-[11px] px-2 py-0.5 ${lockedFields.channels ? 'opacity-60 cursor-not-allowed' : ''}`}
                            onClick={() => {
                              if (lockedFields.channels) return;
                              const id = ch.buffer_profile_id || ch.id;
                              updateForm('channels', selected ? form.channels.filter(c => c !== id) : [...form.channels, id]);
                            }}
                            data-testid={`channel-${ch.id}`}>
                            {ch.platform || ch.service} {ch.username ? `(${ch.username})` : ''}
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Campo 21: Data/ora pubblicazione */}
                <div>
                  <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>
                    Data e ora pubblicazione {lockedFields.scheduled_date && <Lock className="w-3 h-3 inline ml-1" />}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="date" value={form.scheduled_date}
                      onChange={e => updateForm('scheduled_date', e.target.value)}
                      disabled={!!lockedFields.scheduled_date}
                      className="text-xs h-8" data-testid="schedule-date" />
                    <Input type="time" value={form.scheduled_time}
                      onChange={e => updateForm('scheduled_time', e.target.value)}
                      disabled={!!lockedFields.scheduled_time}
                      className="text-xs h-8" data-testid="schedule-time" />
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1 italic">
                    Martedì e mercoledì 8-10 hanno il miglior engagement su LinkedIn
                  </p>
                </div>
              </FormSection>
            </Card>

            {/* ─── SEZIONE 4: BRAND & MEMORIA ─── */}
            <Card style={{ boxShadow: 'var(--shadow-card)', borderRadius: 'var(--radius-soft)', border: 'none' }}>
              <FormSection title="Contesto Brand & Memoria" filled={brandFilled} total={2}>
                {/* Campo 22: Tono di voce */}
                <div>
                  <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>
                    Tono di voce <span className="font-normal">(dal Workspace — modificabile per questo post)</span>
                  </label>
                  <Textarea value={form.tone_override} onChange={e => updateForm('tone_override', e.target.value)}
                    rows={2} className="text-xs" data-testid="tone-input" />
                </div>

                {/* Campo 23: Posizionamento */}
                <div>
                  <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>
                    Posizionamento <span className="font-normal">(dal Workspace — modificabile)</span>
                  </label>
                  <Textarea value={form.positioning_override} onChange={e => updateForm('positioning_override', e.target.value)}
                    rows={2} className="text-xs" data-testid="positioning-input" />
                </div>

                {/* Campo 24: Parole chiave da usare */}
                <div>
                  <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>Parole chiave da usare</label>
                  <div className="flex flex-wrap gap-1 mb-1.5">
                    {brandKeywords.map((kw, i) => (
                      <span key={i} className="rounded-full px-2 py-0.5 text-[10px] font-medium bg-green-100 text-green-800">{kw}</span>
                    ))}
                  </div>
                  <TagInput tags={form.keywords_add} onChange={v => updateForm('keywords_add', v)}
                    placeholder="Aggiungi per questo post..." pillColor="#22C55E" />
                </div>

                {/* Campo 25: Espressioni da evitare */}
                <div>
                  <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>Espressioni da evitare</label>
                  <div className="flex flex-wrap gap-1 mb-1.5">
                    {brandAvoid.map((kw, i) => (
                      <span key={i} className="rounded-full px-2 py-0.5 text-[10px] font-medium bg-red-100 text-red-800">{kw}</span>
                    ))}
                  </div>
                  <TagInput tags={form.keywords_avoid_add} onChange={v => updateForm('keywords_avoid_add', v)}
                    placeholder="Aggiungi per questo post..." pillColor="#EF4444" />
                </div>

                {/* Campo 26: Memory flags */}
                <div>
                  <label className="text-[11px] font-medium flex items-center gap-1 mb-1.5" style={{ color: 'var(--color-muted)' }}>
                    Memoria agente
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-3 h-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-[220px]">
                        <p className="text-xs">Seleziona quali informazioni l'agente Copywriter deve consultare prima di scrivere. Più contesto = risultati più personalizzati.</p>
                      </TooltipContent>
                    </Tooltip>
                  </label>
                  <div className="space-y-1">
                    {MEMORY_FLAGS.map(f => (
                      <label key={f.key} className="flex items-start gap-2 p-2 rounded-lg cursor-pointer transition-colors"
                        style={{ backgroundColor: form.memory_flags[f.key] ? '#F0F7FF' : 'transparent' }}
                        data-testid={`flag-${f.key}`}>
                        <input type="checkbox" checked={!!form.memory_flags[f.key]}
                          onChange={() => setForm(prev => ({ ...prev, memory_flags: { ...prev.memory_flags, [f.key]: !prev.memory_flags[f.key] } }))}
                          className="mt-0.5 rounded" />
                        <div>
                          <span className="text-[12px] font-medium">{f.label}</span>
                          <p className="text-[10px] text-muted-foreground">{f.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </FormSection>
            </Card>

            {/* ─── GENERATE BUTTON ─── */}
            <Button onClick={() => generate()} disabled={generating || !form.topic.trim() || !form.objective_id}
              className="w-full btn-primary" style={{ borderRadius: 'var(--radius-soft)' }}
              data-testid="generate-btn">
              {generating ? (
                <><Loader2 className="w-4 h-4 mr-2 agent-generating" /> Il Copywriter sta scrivendo...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Genera con Copywriter</>
              )}
            </Button>
            {(!form.topic.trim() || !form.objective_id) && (
              <p className="text-[11px] text-center text-muted-foreground -mt-1">Compila tema e obiettivo per generare</p>
            )}
          </div>

          {/* ═══ RIGHT PANEL ═══ */}
          <div className="overflow-y-auto" style={{ maxWidth: 600 }} data-testid="preview-panel">
            {/* Generating state */}
            {generating && (
              <Card className="h-full flex flex-col items-center justify-center" style={{ boxShadow: 'var(--shadow-card)', borderRadius: 'var(--radius-soft)', border: 'none' }}>
                <GenerationSteps step={genStep} />
              </Card>
            )}

            {/* Empty state */}
            {!generating && !result && (
              <Card className="h-full flex items-center justify-center" style={{ boxShadow: 'var(--shadow-card)', borderRadius: 'var(--radius-soft)', border: 'none' }}>
                <EmptyState type="content" title="Preview del contenuto"
                  text="Configura le variabili a sinistra e clicca Genera per creare il contenuto." />
              </Card>
            )}

            {/* Result */}
            {!generating && result && (
              <div className="space-y-3">
                {/* 1. Content Score */}
                {score && (
                  <div className="rounded-xl border p-3" style={{ boxShadow: 'var(--shadow-card)' }} data-testid="content-score">
                    <button onClick={() => setScoreExpanded(!scoreExpanded)} className="w-full flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold"
                        style={{ background: scoreColor }}>{score.total}</div>
                      <div className="flex-1 text-left">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold">Content Score: {score.total}/100</span>
                          <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ color: scoreColor, background: `${scoreColor}15` }}>{score.grade}</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HelpCircle className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[250px]">
                              <p className="text-xs">Il Content Score valuta la qualità del testo su 7 criteri: hook, brand, struttura, lunghezza, hashtag, originalità, lingua. Espandi per i dettagli.</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <div className="w-full h-1.5 bg-gray-200 rounded-full mt-1">
                          <div className="h-full rounded-full transition-all" style={{ width: `${score.total}%`, background: scoreColor }} />
                        </div>
                      </div>
                      {scoreExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                    </button>
                    {scoreExpanded && score.details && (
                      <div className="mt-3 space-y-2 pt-2 border-t">
                        {score.details.map(d => (
                          <div key={d.label} className="flex items-center gap-2">
                            <span className="text-[11px] w-24 text-muted-foreground">{d.label}</span>
                            <div className="flex-1 h-1.5 bg-gray-100 rounded-full">
                              <div className="h-full rounded-full" style={{ width: `${(d.score / d.max) * 100}%`, background: scoreColor }} />
                            </div>
                            <span className="text-[11px] font-medium w-8 text-right">{d.score}/{d.max}</span>
                            {d.note && <span className="text-[9px] text-muted-foreground">{d.note}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* 2. LinkedIn Preview Mockup */}
                <div className="rounded-xl border shadow-sm bg-white" data-testid="linkedin-preview">
                  {/* Header LinkedIn */}
                  <div className="flex items-center gap-3 p-4 border-b">
                    <div className="w-11 h-11 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold">EC</div>
                    <div>
                      <p className="text-sm font-semibold">Emanuele Casero</p>
                      <p className="text-[10px] text-muted-foreground">Founder HumanAImpact</p>
                    </div>
                    <span className="ml-auto text-[10px] px-2 py-1 rounded bg-blue-50 text-blue-600 font-medium">Preview LinkedIn</span>
                  </div>

                  {/* Post text */}
                  <div className="p-5">
                    {editing ? (
                      <div className="space-y-2">
                        <Textarea value={editText} onChange={e => setEditText(e.target.value)} rows={8} className="text-sm leading-[1.85]" data-testid="edit-textarea" />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={saveEdit} data-testid="save-edit-btn">Salva modifiche</Button>
                          <Button size="sm" variant="outline" onClick={() => setEditing(false)}>Annulla</Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap leading-[1.85]" data-testid="generated-text">{result.generated_text}</p>
                    )}
                  </div>

                  {/* Image area */}
                  {selectedImage ? (
                    <div className="mx-5 mb-4 rounded-lg overflow-hidden relative group">
                      <img src={selectedImage.url} alt={selectedImage.name} className="w-full h-36 object-cover" />
                      <button onClick={() => setSelectedImage(null)}
                        className="absolute top-2 right-2 bg-black/50 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="mx-5 mb-4 rounded-lg h-36 border-2 border-dashed flex items-center justify-center cursor-pointer"
                      style={{ borderColor: '#E5E7EB', backgroundColor: '#F9FAFB' }}
                      data-testid="image-area">
                      <div className="text-center">
                        <ImageIcon className="mx-auto h-6 w-6 text-gray-300 mb-1" />
                        <p className="text-[11px] text-muted-foreground">
                          <button onClick={openImageSheet} className="text-blue-600 hover:underline" data-testid="open-image-library">Scegli dalla libreria</button>
                          {' · '}
                          <button onClick={() => fileInputRef.current?.click()} className="text-blue-600 hover:underline" data-testid="upload-image-btn">Carica</button>
                          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* 3. Action buttons */}
                <div className="flex items-center gap-2 flex-wrap" data-testid="action-buttons">
                  <Button size="sm" onClick={approveContent} disabled={result.metadata?.status === 'approved'}
                    className="text-white" style={{ background: 'var(--color-success)', borderRadius: 'var(--radius-soft)' }}
                    data-testid="approve-btn">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approva e programma
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setEditing(true); setEditText(result.generated_text); }}
                    style={{ borderRadius: 'var(--radius-soft)' }} data-testid="edit-btn">
                    <Edit3 className="w-3.5 h-3.5 mr-1" /> Modifica
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setRegenOpen(true)}
                    className="text-blue-600" style={{ borderRadius: 'var(--radius-soft)' }} data-testid="regenerate-btn">
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Rigenera
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setRejectOpen(true)}
                    className="text-red-600 border-red-200 hover:bg-red-50" style={{ borderRadius: 'var(--radius-soft)' }} data-testid="reject-btn">
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Rifiuta
                  </Button>
                  <div className="relative">
                    <Button size="sm" variant="outline" onClick={() => setDuplicateOpen(!duplicateOpen)}
                      style={{ borderRadius: 'var(--radius-soft)' }} data-testid="duplicate-btn">
                      <Send className="w-3.5 h-3.5 mr-1" /> Duplica per... <ChevronDown className="w-3 h-3 ml-1" />
                    </Button>
                    {duplicateOpen && (
                      <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border z-10 min-w-[180px]">
                        {channels.filter(c => !form.channels.includes(c.buffer_profile_id || c.id)).map(c => (
                          <button key={c.id} className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50"
                            onClick={() => duplicate(c.buffer_profile_id || c.id)}>
                            {c.platform || c.service} {c.username ? `(${c.username})` : ''}
                          </button>
                        ))}
                        {channels.length <= 1 && <p className="px-3 py-2 text-xs text-muted-foreground">Nessun altro canale disponibile</p>}
                      </div>
                    )}
                  </div>
                </div>

                {/* 4. Context badges */}
                <div className="flex items-center gap-1.5 flex-wrap" data-testid="context-badges">
                  {result.metadata?.objective && <Badge className="text-[10px] bg-blue-100 text-blue-800 border-0">{result.metadata.objective}</Badge>}
                  {result.metadata?.targets?.map((t, i) => <Badge key={i} className="text-[10px] bg-purple-100 text-purple-800 border-0">{t}</Badge>)}
                  {result.metadata?.rubrica && <Badge className="text-[10px] bg-orange-100 text-orange-800 border-0">{result.metadata.rubrica}</Badge>}
                  {result.metadata?.event_name && <Badge className="text-[10px] bg-green-100 text-green-800 border-0">{result.metadata.event_name}</Badge>}
                  {result.metadata?.channel && <Badge variant="outline" className="text-[10px]">{result.metadata.channel}</Badge>}
                  {result.metadata?.format_code && <Badge variant="outline" className="text-[10px]">{result.metadata.format_code}</Badge>}
                </div>
              </div>
            )}
          </div>
        </div>

        </>)}
        {/* ═══ SHEETS & DIALOGS ═══ */}

        {/* Scout Sheet */}
        <Sheet open={scoutOpen} onOpenChange={setScoutOpen}>
          <SheetContent side="right" className="w-[400px] sm:max-w-[400px]">
            <SheetHeader>
              <SheetTitle>Deep Research</SheetTitle>
            </SheetHeader>
            <div className="mt-4 space-y-3 overflow-y-auto max-h-[calc(100vh-120px)]">
              {scoutReports.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nessuna ricerca recente</p>
              ) : scoutReports.map(report => (
                <div key={report.id} className="border rounded-lg p-3">
                  <button onClick={() => setExpandedReport(expandedReport === report.id ? null : report.id)}
                    className="w-full text-left">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium">{report.title || report.query}</span>
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expandedReport === report.id ? 'rotate-180' : ''}`} />
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      {report.created_at ? new Date(report.created_at).toLocaleDateString('it-CH') : ''} · {report.results?.length || 0} risultati
                    </div>
                  </button>
                  {expandedReport === report.id && report.results && (
                    <div className="mt-2 space-y-2 border-t pt-2">
                      {report.results.map((r, i) => (
                        <div key={i} className="p-2 rounded bg-gray-50">
                          <p className="text-[11px] font-medium">{r.title}</p>
                          {r.source && <p className="text-[9px] text-muted-foreground">{r.source}</p>}
                          {r.summary && <p className="text-[10px] mt-1">{r.summary}</p>}
                          <button onClick={() => {
                            updateForm('inspiration_source', `${r.url || r.source || ''} - ${r.summary || r.title}`);
                            setScoutOpen(false);
                          }} className="text-[10px] text-blue-600 hover:underline mt-1">
                            Usa come fonte
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <button onClick={() => { setScoutOpen(false); navigate('/research'); }}
                className="w-full text-center text-xs text-blue-600 hover:underline py-2">
                Apri Deep Research
              </button>
            </div>
          </SheetContent>
        </Sheet>

        {/* Image Library Sheet */}
        <Sheet open={imageSheetOpen} onOpenChange={setImageSheetOpen}>
          <SheetContent side="right" className="w-[400px] sm:max-w-[400px]">
            <SheetHeader>
              <SheetTitle>Libreria immagini</SheetTitle>
            </SheetHeader>
            <div className="mt-4 space-y-3">
              <div className="flex gap-2">
                <Input placeholder="Cerca..." value={imageSearch}
                  onChange={e => setImageSearch(e.target.value)} className="text-xs" />
                <Select value={imageFolder} onValueChange={async (v) => {
                  setImageFolder(v);
                  try {
                    const res = await api.get(`/designer/assets?type=image&folder=${v}&limit=20`);
                    setImageAssets(res.data || []);
                  } catch {}
                }}>
                  <SelectTrigger className="text-xs w-[100px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte</SelectItem>
                    <SelectItem value="brand">Brand</SelectItem>
                    <SelectItem value="social">Social</SelectItem>
                    <SelectItem value="events">Eventi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2 overflow-y-auto max-h-[calc(100vh-220px)]">
                {imageAssets.filter(img => !imageSearch || img.name?.toLowerCase().includes(imageSearch.toLowerCase())).map(img => (
                  <div key={img.id} onClick={() => { setSelectedImage(img); setImageSheetOpen(false); }}
                    className="rounded-lg overflow-hidden cursor-pointer border-2 hover:border-blue-500 transition-colors">
                    <img src={img.url || img.secure_url} className="w-full h-24 object-cover" alt={img.name} />
                    <p className="text-[10px] p-1 truncate">{img.name || 'Immagine'}</p>
                  </div>
                ))}
                {imageAssets.length === 0 && <p className="col-span-2 text-xs text-muted-foreground text-center py-4">Nessuna immagine trovata</p>}
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* Reject Dialog */}
        <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Rifiuta contenuto</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2">
              <Textarea placeholder="Perché rifiuti questo contenuto? (obbligatorio)" value={rejectNote}
                onChange={e => setRejectNote(e.target.value)} rows={3} data-testid="reject-note" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setRejectOpen(false)}>Annulla</Button>
                <Button onClick={rejectContent} disabled={!rejectNote.trim()} className="bg-red-600 hover:bg-red-700 text-white" data-testid="reject-confirm">Rifiuta</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Regenerate Dialog */}
        <Dialog open={regenOpen} onOpenChange={setRegenOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Rigenera contenuto</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2">
              <Textarea placeholder="Cosa vuoi cambiare? (opzionale)" value={regenNote}
                onChange={e => setRegenNote(e.target.value)} rows={3} data-testid="regen-note" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setRegenOpen(false)}>Annulla</Button>
                <Button onClick={regenerate} data-testid="regen-confirm">
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Rigenera
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Save Template Dialog */}
        <Dialog open={showSaveTemplate} onOpenChange={setShowSaveTemplate}>
          <DialogContent>
            <DialogHeader><DialogTitle>Salva Template</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2">
              <Input placeholder="Nome del template..." value={templateName}
                onChange={e => setTemplateName(e.target.value)} data-testid="template-name-input" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowSaveTemplate(false)}>Annulla</Button>
                <Button onClick={saveTemplate} data-testid="save-template-confirm">Salva</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Config Dialog */}
        <ConfigDialog open={!!configDialogType} onClose={() => setConfigDialogType(null)}
          configType={configDialogType} onUpdate={loadConfig} />
      </div>
    </TooltipProvider>
  );
}
